import {
  SUPPORT_PHONE_DISPLAY,
  SUPPORT_PHONE_HREF,
  ZoomBookingFrame,
  isEmbeddableBookingUrl,
} from '@nrx/booking-ui';
import type { ZoomBookedPayload } from '@nrx/booking-ui';
import { Alert, Link, Text } from '@nrx/ui';
import { type JSX, useState } from 'react';

import { notifyConsultBooked } from '../../lib/ffs-api.js';

/**
 * Embeds the shared Zoom scheduler frame; owns the post-booking notify call + confirmation card.
 *
 * @remarks `onUnresponsive` is wired here as well as in apps/consult (review finding 12). The
 * 8-second dead-frame detection is optional on `ZoomBookingFrame`, so omitting it compiled cleanly
 * and failed no test — but this is the LIVE production surface with exactly the exposure the
 * detection exists for: if `ffs.<apex>` is not registered as a Zoom "Add to Website" origin, the
 * frame renders, the member books, and no confirmation ever appears. Silent failure with no
 * affordance was the pre-PR state on this surface; it is closed here rather than inherited.
 *
 * The escape-hatch link renders only for an absolute http(s) `bookingUrl`, for the reason recorded
 * on `isEmbeddableBookingUrl`: a schemeless operator value is a RELATIVE url, so an anchor carrying
 * it resolves against this app's own origin and opens the app again rather than Zoom.
 */
export function BookingStep({
  bookingUrl,
  member,
  patientId,
  onBooked,
}: {
  bookingUrl: string;
  member: { email: string; firstName: string; lastName: string; phone: string };
  patientId?: string;
  /** Fired once the Zoom bookingForm completion is received (lets the page update its heading). */
  onBooked?: () => void;
}): JSX.Element {
  const [booked, setBooked] = useState(false);
  const [unresponsive, setUnresponsive] = useState(false);
  const linkable = isEmbeddableBookingUrl(bookingUrl);

  if (booked) {
    return (
      <div className="nrx-card flex flex-col items-center gap-4 rounded-[var(--radius-lg)] px-8 py-14 text-center">
        <span
          className="flex h-14 w-14 items-center justify-center rounded-full bg-[var(--chip-green)] text-2xl font-bold text-white"
          aria-hidden="true"
        >
          ✓
        </span>
        <p className="text-xl font-semibold text-[var(--forest)]">Your consult is booked!</p>
        <p className="max-w-[40ch] text-[var(--ink-soft)]">
          Check your email for the confirmation and calendar invite.
        </p>
      </div>
    );
  }

  return (
    <div>
      <ZoomBookingFrame
        bookingUrl={bookingUrl}
        member={member}
        title="Schedule your consult"
        onBooked={({ scheduledEventId, attendeeId }: ZoomBookedPayload) => {
          void notifyConsultBooked({
            scheduledEventId,
            attendeeId,
            ...(patientId !== undefined ? { patientId } : {}),
          });
          setBooked(true);
          onBooked?.();
        }}
        onUnresponsive={() => setUnresponsive(true)}
      />
      {unresponsive ? (
        <Alert tone="warning" className="mt-4 text-center">
          {linkable ? (
            <Text as="p">
              Having trouble loading the scheduler?{' '}
              <Link href={bookingUrl} target="_blank" rel="noopener noreferrer" underline>
                <Text weight="medium">Book in a new tab</Text>
              </Link>
            </Text>
          ) : (
            <>
              <Text as="p">
                We can&apos;t open the scheduler for this consult. Please call us at{' '}
                <Link href={SUPPORT_PHONE_HREF} underline>
                  <Text weight="medium">{SUPPORT_PHONE_DISPLAY}</Text>
                </Link>{' '}
                and we&apos;ll book it with you.
              </Text>
              <Text as="p" size="2" color="muted">
                Scheduler address on file: {bookingUrl}
              </Text>
            </>
          )}
        </Alert>
      ) : null}
    </div>
  );
}
